<template>
  <ak-drawer
    :active="service.active.value"
    has-backdrop
    show-overlay
    show-highest
    paddingless
    min-width="50%"
    @close="service.reset"
  >
    <template #body>
      <ak-form class="h-full" @submit="handleSubmit()">
        <div class="is-scrollable h-full flex flex--column">
          <!-- header -->
          <div class="box pb-small sticky sticky--top bb bg-white z-index--4">
            <h3 class="color-primary mb-tiny">SFTP Profile</h3>
          </div>

          <!-- body -->
          <div
            v-if="mode !== 'post-create'"
            class="sftp-profile flex__item box"
          >
            <div v-if="profile">
              <!-- profile name -->
              <ak-field label="Name">
                <ak-input
                  v-model="profile.name"
                  placeholder="Enter name"
                  maxlength="100"
                  aria-label="profile name"
                  required
                />
              </ak-field>

              <!-- username -->
              <ak-field label="Username">
                <ak-integration-s-f-t-p-profile-saved-field
                  :locked="lockedFields.username"
                  reference="Username"
                  @on-unlock="unlockField('username')"
                >
                  <ak-input
                    v-model="profile.username"
                    placeholder="Enter username"
                    aria-label="profile username"
                    required
                  />
                </ak-integration-s-f-t-p-profile-saved-field>
              </ak-field>

              <!-- authentication type -->
              <ak-field label="Authentication Type">
                <ak-select
                  v-if="mode === 'create'"
                  v-model="authenticationType"
                  :options="authenticationTypes"
                  :disabled="mode !== 'create'"
                  track-by="value"
                  label="label"
                  required
                />
                <p v-else class="text-bold color-action">
                  {{ authenticationType && authenticationType.label }}
                </p>
              </ak-field>

              <!-- password -->
              <ak-field v-if="profile.type === 'password'" label="Password">
                <ak-integration-s-f-t-p-profile-saved-field
                  :locked="lockedFields.password"
                  reference="Password"
                  @on-unlock="unlockField('password')"
                >
                  <ak-input
                    v-model="profile.password"
                    placeholder="Enter password"
                    required
                  />
                </ak-integration-s-f-t-p-profile-saved-field>
              </ak-field>

              <!-- public key -->
              <ak-field
                v-if="
                  profile.type === 'key' &&
                  (mode === 'create' || profile.publicKey)
                "
                label="Public Key"
              >
                <div class="flex">
                  <ak-notification
                    v-if="mode === 'create'"
                    :closable="false"
                    class="b-none"
                    type="info"
                    has-icon
                  >
                    A public key will be issued on creation of this profile.
                  </ak-notification>
                  <ak-integration-s-f-t-p-profile-public-key
                    v-if="profile.publicKey"
                    :public-key="profile.publicKey"
                  />
                </div>
              </ak-field>

              <!-- host + port -->
              <ak-pack :alignment="'top'">
                <ak-pack-item :grow="true">
                  <ak-field label="Host">
                    <ak-input
                      v-model="profile.host"
                      placeholder="Enter host"
                      required
                    />
                  </ak-field>
                </ak-pack-item>
                <ak-pack-item>
                  <ak-field label="Port">
                    <ak-input
                      v-model="port"
                      type="number"
                      min="1"
                      max="65535"
                      required
                    />
                  </ak-field>
                </ak-pack-item>
              </ak-pack>
            </div>
          </div>

          <!-- post create -->
          <div
            v-else
            class="sftp-profile sftp-profile--post-create flex__item box"
          >
            <div v-if="profile.publicKey">
              <h4 class="color-action text-center mb">Public Key</h4>
              <ak-integration-s-f-t-p-profile-public-key
                :public-key="profile.publicKey"
              />
            </div>
          </div>

          <!-- footer -->
          <div
            class="sticky sticky--bottom stacked pt-tiny mt-small z-index--top"
          >
            <ak-button
              v-if="mode !== 'post-create'"
              size="large"
              class="one-whole radius-none"
              type="submit"
              :disabled="service.working.value"
              >Save</ak-button
            >
            <ak-button
              v-else
              size="large"
              class="one-whole radius-none"
              @click="service.reset()"
              >Close</ak-button
            >
          </div>
        </div>
      </ak-form>
    </template>
  </ak-drawer>
</template>

<script>
import { computed, defineComponent, reactive, ref, watch } from 'vue'

import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Notification from '@akerolabs/akero-vue/src/components/notification/Notification.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'

import IntegrationSFTPProfilePublicKey from './IntegrationSFTPProfilePublicKey.vue'
import IntegrationSFTPProfileSavedField from './IntegrationSFTPProfileSavedField.vue'

export default defineComponent({
  name: 'AkIntegrationSFTPProfile',
  components: {
    [Box.name]: Box,
    [Button.name]: Button,
    [Field.name]: Field,
    [Input.name]: Input,
    [Notification.name]: Notification,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Select.name]: Select,
    [IntegrationSFTPProfilePublicKey.name]: IntegrationSFTPProfilePublicKey,
    [IntegrationSFTPProfileSavedField.name]: IntegrationSFTPProfileSavedField,
  },
  provide() {
    return {
      akForm: undefined, // stub unneeded dependency
    }
  },
  props: {
    service: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const mode = computed(() => props.service.mode.value)
    const profile = ref(null)
    const lockedFields = reactive({})

    /**
     * Reset locked fields on mode change
     * @param {boolean} value
     */
    const resetLockedFields = (value) =>
      ['username', 'password'].forEach((field) => (lockedFields[field] = value))

    /**
     * Determine if locked fields should be used
     * @type {ComputedRef<boolean>}
     */
    const useLockedFields = computed(() => mode.value !== 'create')

    /**
     * Unlock the given field
     * @param {String} field
     */
    const unlockField = (field) => (lockedFields[field] = false)

    /**
     * List of authentication types
     * @type {Array<Object>}
     */
    const authenticationTypes = [
      { value: 'password', label: 'Password', lockedFieldRef: 'password' },
      { value: 'key', label: 'Public Key' },
    ]

    /**
     * Selected authentication type
     * @type {WritableComputedRef<T>}
     */
    const authenticationType = computed({
      get: () =>
        authenticationTypes.find((entry) => entry.value === profile.value.type),
      set: (entry) => {
        if (!entry) return
        profile.value.type = entry.value
        if (useLockedFields.value && entry.lockedFieldRef) {
          // force re-entry on type change
          lockedFields[entry.lockedFieldRef] = false
          profile[entry.lockedFieldRef] = ''
        }
      },
    })

    /**
     * Port update with type conversion
     * @type {WritableComputedRef<*>}
     */
    const port = computed({
      get: () => profile.value.port,
      set: (value) =>
        (profile.value.port = !isNaN(value) ? Number(value) : value),
    })

    /**
     * Handle form submission
     */
    const handleSubmit = () => {
      const usePostCreate =
        mode.value === 'create' &&
        authenticationType.value &&
        authenticationType.value.value === 'key'
      props.service.process(
        !usePostCreate,
        usePostCreate ? 'post-create' : null,
      )
    }

    watch(
      () => props.service.active.value,
      () => (profile.value = props.service.profile),
    )

    watch(
      () => props.service.mode.value,
      (value) => {
        profile.value = props.service.profile
        resetLockedFields(value !== 'create')
      },
    )

    return {
      mode,
      profile,
      authenticationType,
      authenticationTypes,
      port,
      lockedFields,
      unlockField,
      handleSubmit,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@akerolabs/akero-vue/src/scss/_settings.scss';

.sftp-profile {
  &--post-create {
    display: grid;
    place-items: center;
  }

  :deep(.ak-notify) {
    box-shadow: none !important;
  }
}
</style>
